/**
 * 视频生成 API 客户端
 *
 * 完全独立于图片端的 services/api.ts，避免类型污染。
 *
 * 包含模块：
 * - 任务相关（generations / 列表 / 详情 / 删除 / preview）
 * - 模型/Provider 元数据
 * - 渠道、模型、系统配置（管理员）
 * - 存储签名 URL 获取
 *
 * 错误响应统一格式：成功 `{ ok: true, data }`，失败 `{ error: '...' }`
 */

import { API_BASE, authFetch, readData } from './apiBase';

// ---------------- 类型定义 ----------------

export type VideoMode = 'text2video' | 'img2video' | 'first_last_frame';
export type VideoStatus = 'pending' | 'submitting' | 'processing' | 'completed' | 'failed' | 'timeout';
export type CameraMovement = 'static' | 'pan_left' | 'pan_right' | 'zoom_in' | 'zoom_out' | 'tilt_up' | 'tilt_down';
export type MotionIntensity = 'low' | 'medium' | 'high';
export type VideoProviderType =
  | 'apib-grok-imagine-1-5'
  | 'apib-omni-flash-ext'
  | 'apib-seedance-1-0'
  | 'apib-seedance-1-5'
  | 'apib-seedance-2-0'
  | 'apib-seedance-2-5'
  | 'apib-wan-2-6-flash'
  | 'apib-wan-3-0'
  | 'apib-hailuo-2-3-fast'
  | 'apib-veo-3-1-fast-ext'
  | 'apib-kling-v3'
  | 'apib-sora-2'
  | 'apib-happyhorse-1-1'
  | 'apib-minimax-h3'
  | 'apib-vidu-q3-turbo'
  | 'apib-vidu-q3';

export interface VideoGenerateRequestBody {
  modelId: string;
  mode: VideoMode;
  prompt: string;
  duration: number;
  resolution: string;
  aspectRatio: string;
  cameraMovement?: CameraMovement;
  motionIntensity?: MotionIntensity;
  enableAudio?: boolean;
  /** 参考图列表，可以是 storage key、http URL 或 data:URL */
  referenceImages?: string[];
  firstFrameImage?: string;
  lastFrameImage?: string;
  /** 参考音频公网 URL 列表 */
  audioReferenceUrls?: string[];
  /** 参考视频公网 URL 列表 */
  videoReferenceUrls?: string[];
  /** 参考视频时长列表（秒，与 videoReferenceUrls 下标对齐；服务端用于输入侧计费） */
  videoReferenceDurations?: number[];
  /** 一次提交几条，默认 1，上限受 maxJobsPerSubmit */
  n?: number;
  /** 客户端幂等键：同键重放返回首次创建的任务，防止重复扣积分 */
  clientRequestId?: string;
}

export interface VideoGenerateResponse {
  jobIds: string[];
  creditsUsed: number;
  creditsPerJob: number;
}

export interface VideoCreditsPreviewBody {
  modelId: string;
  mode: VideoMode;
  duration: number;
  resolution: string;
  n?: number;
  /** 是否开启音频生成（乘音频倍率） */
  enableAudio?: boolean;
  /** 参考视频总时长（秒，输入侧计费） */
  refVideoDurationSec?: number;
  /** 参考图张数（含首尾帧，输入侧计费） */
  refImageCount?: number;
}

export interface VideoCreditsBreakdown {
  output: number;
  refVideo: number;
  refImage: number;
  total: number;
}

export interface VideoCreditsPreviewResponse {
  creditsPerJob: number;
  creditsTotal: number;
  breakdown?: VideoCreditsBreakdown;
}

export interface VideoJobUserView {
  id: string;
  prompt: string;
  model: string;
  mode: VideoMode;
  status: VideoStatus;
  duration: number;
  resolution: string;
  aspectRatio: string;
  cameraMovement: CameraMovement | null;
  motionIntensity: MotionIntensity | null;
  enableAudio: boolean;
  creditsUsed: number;
  resultDuration: number | null;
  resultWidth: number | null;
  resultHeight: number | null;
  resultFileSize: number | null;
  resultMimeType: string | null;
  errorMessage: string | null;
  hasError: boolean;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  /** 列表接口返回的视频直连 URL（供“作为参考视频”等业务复用） */
  resultUrl?: string | null;
  /** 列表接口返回的封面直连 URL */
  coverUrl?: string | null;
  /** 列表接口返回的视频代理播放 URL */
  playUrl?: string | null;
  /** 列表接口返回的视频代理下载 URL */
  downloadUrl?: string | null;
  /** 列表接口返回的封面代理 URL */
  coverProxyUrl?: string | null;
  /**
   * 参考图列表，已解析为前端可访问的 URL（短期签名 URL / http URL / data:URL）。
   * img2video 模式下生效；其它模式为空数组。
   */
  referenceImages?: string[];
  /** first_last_frame 模式下的首帧 URL */
  firstFrameImage?: string | null;
  /** first_last_frame 模式下的尾帧 URL */
  lastFrameImage?: string | null;
  /** 参考视频 URL 列表 */
  videoReferenceUrls?: string[];
}

export interface VideoJobDetail extends VideoJobUserView {
  resultUrl: string | null;
  coverUrl: string | null;
  playUrl: string | null;
  downloadUrl: string | null;
  coverProxyUrl: string | null;
  referenceImages: string[];
  firstFrameImage: string | null;
  lastFrameImage: string | null;
  videoReferenceUrls: string[];
}

export interface VideoModeCapabilityFlags {
  audio?: boolean;
  audioReference?: boolean;
  videoReference?: boolean;
  cameraMovement?: boolean;
  motionIntensity?: boolean;
}

export interface DurationSpecEnum {
  type: 'enum';
  values: number[];
}

export interface DurationSpecRange {
  type: 'range';
  min: number;
  max: number;
  step: number;
}

export type DurationSpec = DurationSpecEnum | DurationSpecRange;

export interface VideoModelModeInfo {
  mode: VideoMode;
  capabilities: VideoModeCapabilityFlags;
  supportedDurations: DurationSpec;
  supportedResolutions: string[];
  supportedAspectRatios: string[];
  maxReferenceImageCount: number;
  allowedReferenceImageCounts?: number[];
  maxAudioReferenceCount: number;
  /** 参考音频最小时长（秒），未设置则不限制 */
  audioReferenceMinDurationSec?: number;
  /** 参考音频最大时长（秒），未设置则不限制 */
  audioReferenceMaxDurationSec?: number;
  maxVideoReferenceCount: number;
  /** 参考视频单条最小时长（秒），未设置则不限制 */
  videoReferenceMinDurationSec?: number;
  /** 参考视频单条最大时长（秒），未设置则不限制 */
  videoReferenceMaxDurationSec?: number;
  /** 多条参考视频累计总时长上限（秒），未设置则不限制 */
  videoReferenceTotalMaxDurationSec?: number;
}

/** 模型所属公司（公开接口返回，用于前端按公司分组） */
export interface VideoCompanyInfo {
  id: string;
  label: string;
}

export interface VideoModelPublicInfo {
  id: string;
  modelId: string;
  name: string;
  description: string;
  sortOrder: number;
  /** 模型实际使用的渠道 provider（公司归属来源） */
  provider?: VideoProviderType | null;
  company?: VideoCompanyInfo | null;
  billingMode: 'per_second' | 'per_call';
  creditsPerSecond: number;
  creditsPerCall: number;
  resolutionMultipliers: Record<string, number>;
  disabledResolutions?: string[];
  modeMultipliers: Record<string, number>;
  modes: VideoModelModeInfo[];
  defaults: {
    duration: number;
    resolution: string;
    aspectRatio: string;
  };
}

export interface VideoProviderMetaInfo {
  id: VideoProviderType;
  label: string;
  badgeLabel: string;
  badgeClassName: string;
  modeSupport: { text2video: boolean; img2video: boolean; first_last_frame: boolean };
  capabilitySupport: {
    audio: boolean;
    audioReference: boolean;
    videoReference: boolean;
    cameraMovement: boolean;
    motionIntensity: boolean;
  };
  supportedDurations: DurationSpec;
  supportedResolutions: string[];
  supportedAspectRatios: string[];
  maxReferenceImageCount: number;
  allowedReferenceImageCounts?: number[];
  maxAudioReferenceCount: number;
  /** 参考音频最小时长（秒），未设置则不限制 */
  audioReferenceMinDurationSec?: number;
  /** 参考音频最大时长（秒），未设置则不限制 */
  audioReferenceMaxDurationSec?: number;
  maxVideoReferenceCount: number;
  /** 参考视频单条最小时长（秒），未设置则不限制 */
  videoReferenceMinDurationSec?: number;
  /** 参考视频单条最大时长（秒），未设置则不限制 */
  videoReferenceMaxDurationSec?: number;
  /** 多条参考视频累计总时长上限（秒），未设置则不限制 */
  videoReferenceTotalMaxDurationSec?: number;
  /** 是否要求图片必须是公网 URL（不支持本地存储 / base64） */
  requiresPublicReferenceUrl?: boolean;
  adminCapability: string;
  adminDetail: string;
  /** 该 provider 支持的上游模型名称列表 */
  supportedModelIds: string[];
  /** 是否被管理员在「视频 Adapter 管理」中隐藏 */
  isHidden?: boolean;
}

export interface VideoListResponse {
  data: VideoJobUserView[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface ListMyVideosParams {
  page?: number;
  pageSize?: number;
  status?: VideoStatus;
}

// ---------------- 视频任务接口 ----------------

export const videoApi = {
  /**
   * 创建视频任务（一次最多 maxJobsPerSubmit 条）
   */
  async generate(body: VideoGenerateRequestBody): Promise<VideoGenerateResponse> {
    const response = await authFetch('/videos/generations', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return readData<VideoGenerateResponse>(response, '创建视频任务失败');
  },

  /**
   * 画布应用的视频生成入口（source='canvas'，任务记录与流水可区分应用来源）
   */
  async generateFromCanvas(body: VideoGenerateRequestBody): Promise<VideoGenerateResponse> {
    const response = await authFetch('/canvas/videos/generations', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return readData<VideoGenerateResponse>(response, '创建视频任务失败');
  },

  /**
   * 单条任务详情（含视频 URL）
   */
  async getJob(id: string): Promise<VideoJobDetail> {
    const response = await authFetch(`/videos/${encodeURIComponent(id)}`);
    return readData<VideoJobDetail>(response, '获取视频详情失败');
  },

  /**
   * 我的视频列表（分页）
   */
  async listMyJobs(params: ListMyVideosParams = {}): Promise<VideoListResponse> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.status) query.set('status', params.status);
    const response = await authFetch(`/videos?${query.toString()}`);
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error((data && data.error) || '获取视频列表失败');
    }
    return {
      data: data?.data || [],
      pagination: data?.pagination || { page: 1, pageSize: 20, total: 0, totalPages: 0 },
    };
  },

  /**
   * 软删除（隐藏）
   */
  async deleteJob(id: string): Promise<void> {
    const response = await authFetch(`/videos/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || '删除视频失败');
    }
  },

  /**
   * 预算积分（含 N 倍）
   */
  async previewCredits(body: VideoCreditsPreviewBody): Promise<VideoCreditsPreviewResponse> {
    const response = await authFetch('/videos/credits/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return readData<VideoCreditsPreviewResponse>(response, '预算积分失败');
  },

  // ---------------- 模型 / Provider 元数据 ----------------

  /**
   * 公开的模型列表（前端表单用）
   */
  async getPublicModels(): Promise<VideoModelPublicInfo[]> {
    const response = await fetch(`${API_BASE}/video-models/public`);
    return readData<VideoModelPublicInfo[]>(response, '获取视频模型列表失败');
  },

  /**
   * 公开的 Provider 元数据
   */
  async getProviderMetadata(): Promise<VideoProviderMetaInfo[]> {
    const response = await fetch(`${API_BASE}/video-models/providers/meta`);
    return readData<VideoProviderMetaInfo[]>(response, '获取 Provider 元数据失败');
  },

  /**
   * 视频系统配置（公开版，仅返回前端真正需要的字段）。
   * 普通用户也能调（仅需登录），用于让 VideoPage 拿到 maxJobsPerSubmit 等参数，
   * 而不必 fallback 写死 4。
   */
  async getPublicSystemConfig(): Promise<{
    maxJobsPerSubmit: number;
    maxConcurrentPerUser: number;
    defaultModelId: string | null;
  }> {
    const response = await authFetch('/video-system-config/public');
    return readData(response, '获取视频系统配置失败');
  },

  // ---------------- 轮询（前端工作台使用） ----------------

  /**
   * 轮询任务直到进入终态
   *
   * @param jobId 任务 ID
   * @param onUpdate 每次拿到状态都会回调（用于 UI 实时更新）
   * @param options.intervalMs 轮询间隔（默认 5000ms）
   * @param options.maxAttempts 最大轮询次数（默认 240，约 20 分钟兜底）
   */
  async pollJobStatus(
    jobId: string,
    onUpdate: (job: VideoJobDetail) => void,
    options: { intervalMs?: number; maxAttempts?: number } = {}
  ): Promise<VideoJobDetail> {
    const intervalMs = options.intervalMs ?? 5000;
    const maxAttempts = options.maxAttempts ?? 240;

    let attempts = 0;
    return new Promise<VideoJobDetail>((resolve, reject) => {
      const tick = async () => {
        attempts++;
        try {
          const job = await this.getJob(jobId);
          onUpdate(job);

          if (job.status === 'completed') return resolve(job);
          if (job.status === 'failed' || job.status === 'timeout') {
            return reject(new Error(job.errorMessage || (job.status === 'timeout' ? '视频生成超时' : '视频生成失败')));
          }
          if (attempts >= maxAttempts) {
            return reject(new Error('等待超时，请稍后刷新查看结果'));
          }
          setTimeout(tick, intervalMs);
        } catch (err) {
          reject(err as Error);
        }
      };
      tick();
    });
  },
};
